import React from 'react';
import { BrowserRouter, Routes, Route, NavLink, useLocation } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardPage from './pages/DashboardPage';
import FeedsPage from './pages/FeedsPage';
import ShopRoomPage from './pages/ShopRoomPage';
import AddShopsPage from './pages/AddShopsPage';
import UploadFeedPage from './pages/UploadFeedPage';
import ConnectFeedPage from './pages/ConnectFeedPage';
import NetworksPage from './pages/NetworksPage';
import EmbedPage from './pages/EmbedPage';
import DebugPage from './pages/DebugPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AuditReportPage from './pages/AuditReportPage';
import BrandLogo from './components/BrandLogo';

function NavBar() {
  const { user, logout, verificationStatus } = useAuth();
  const status = verificationStatus || 'pending';
  // Mobile nav is a hamburger-toggled stacked panel, never a horizontal
  // scroll strip — the last tab used to be unreachable off the right edge.
  // Above 900px CSS puts the panel back inline (display:contents) and this
  // flag stops mattering.
  const [menuOpen, setMenuOpen] = React.useState(false);
  const closeMenu = () => setMenuOpen(false);
  const navClass = ({ isActive }: { isActive: boolean }) => `nav-link${isActive ? ' active' : ''}`;

  return (
    <header className="App-header">
      <div className="topbar">
        <a className="brand" href="https://imgaff.com" aria-label="ImgAff website">
          <BrandLogo />
        </a>
        <button
          type="button"
          className="nav-toggle"
          aria-expanded={menuOpen}
          aria-controls="portal-menu"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="visually-hidden">{menuOpen ? 'Close menu' : 'Menu'}</span>
        </button>
        <div className="topbar-panel" id="portal-menu" data-open={menuOpen ? 'true' : 'false'}>
          <nav className="topbar-nav" aria-label="Publisher portal">
            <NavLink to="/" end className={navClass} onClick={closeMenu}>Overview</NavLink>
            <NavLink to="/analytics" className={navClass} onClick={closeMenu}>Analytics</NavLink>
            <NavLink to="/feeds" className={navClass} onClick={closeMenu}>Product feeds</NavLink>
            <NavLink to="/embed" className={navClass} onClick={closeMenu}>Install</NavLink>
            {/* Route path stays /debug so bookmarks/links survive; only the
                visible label uses the troubleshooting vocabulary. */}
            <NavLink to="/debug" className={navClass} onClick={closeMenu}>Fix matches</NavLink>
          </nav>
          {user && (
            <div className="user-info">
              {/* Labeled (never a bare "pending"). While pending/failed it links
                  to the dashboard's verification workflow (plain anchor on
                  purpose: the browser scrolls to #website-verification natively
                  on load). Once verified the workflow section no longer renders,
                  so the badge is plain text — never a dead anchor. */}
              {status === 'verified' ? (
                <span
                  className={`status-${status}`}
                  data-testid="verification-status"
                  title={`Website verification status: ${status}`}
                >
                  Site verified
                </span>
              ) : (
                <a
                  href="/#website-verification"
                  className={`status-${status}`}
                  data-testid="verification-status"
                  title={`Website verification status: ${status}`}
                  style={{ textDecoration: 'none' }}
                >
                  Verify site
                </a>
              )}
              <span className="user-email">{user.email}</span>
              <button data-testid="logout-button" className="logout-btn" onClick={logout}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// Every route rendered the same <title> and no <h1> at all, and focus never
// moved on navigation — so for a keyboard or screen-reader user the portal had
// five pages that were indistinguishable from each other and from not having
// navigated. The heading is visually hidden because each page already shows its
// own <h2>; this adds the missing document-level name without redesigning them.
const PAGE_NAMES: Record<string, string> = {
  '/': 'Overview',
  '/analytics': 'Analytics',
  '/feeds': 'Product feeds',
  '/networks': 'Your networks',
  '/embed': 'Install',
  '/debug': 'Fix matches',
};

/**
 * The name for a path, including the ones that carry an id.
 *
 * `PAGE_NAMES` is an exact-path map, so `/feeds/feed:12` would fall through to
 * the generic name and undo, for that route, the whole point of the heading —
 * a page indistinguishable from not having navigated.
 */
function pageName(pathname: string): string {
  if (PAGE_NAMES[pathname]) return PAGE_NAMES[pathname];
  if (pathname === '/feeds/add') return 'Shops you could add';
  if (pathname === '/feeds/upload') return 'Upload a feed file';
  if (pathname === '/feeds/connect') return 'Connect a feed by URL';
  if (pathname.startsWith('/feeds/')) return 'Shop details';
  return 'Publisher portal';
}

function PageHeading() {
  const { pathname } = useLocation();
  const ref = React.useRef<HTMLHeadingElement>(null);
  const firstRender = React.useRef(true);
  const name = pageName(pathname);

  React.useEffect(() => {
    document.title = `${name} · ImgAff Publisher Portal`;
    // Not on first paint: taking focus off the document start on a cold load
    // is disorienting and skips the skip link. Only a route CHANGE moves it.
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    ref.current?.focus();
  }, [pathname, name]);

  return <h1 ref={ref} tabIndex={-1} className="visually-hidden">{name}</h1>;
}

function AppContent() {
  return (
    <div data-testid="user-dashboard" className="App">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <NavBar />
      <main className="dashboard" id="main-content" tabIndex={-1}>
        <PageHeading />
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/feeds" element={<FeedsPage />} />
          {/* The three doors. STATIC SEGMENTS FIRST — React Router ranks them
              above the dynamic one, and `ShopRoomPage` additionally refuses
              these three words, so a future shop keyed `upload` cannot take a
              screen's URL. Every future `/feeds/<static>` must be added to that
              refusal list too. */}
          <Route path="/feeds/add" element={<AddShopsPage />} />
          <Route path="/feeds/upload" element={<UploadFeedPage />} />
          <Route path="/feeds/connect" element={<ConnectFeedPage />} />
          {/* One shop, whole. No nav item: the nav is deliberately five, and
              every Feeds surface is reached from `/feeds`. `:ref` is
              `feed:<feedId>` or `<network>:<programmeKey>` — never `Shop.key`,
              which re-keys under the publisher. See `utils/shopRef.ts`. */}
          <Route path="/feeds/:ref" element={<ShopRoomPage />} />
          <Route path="/networks" element={<NetworksPage />} />
          <Route path="/embed" element={<EmbedPage />} />
          <Route path="/debug" element={<DebugPage />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Outside ProtectedRoute on purpose: redeeming the emailed link is
              what creates the session, so requiring one first would be
              circular. The only route in the portal with that property. */}
          <Route path="/audit/open" element={<AuditReportPage />} />
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <AppContent />
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
